import { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import { AlertTriangle, Check, CircleAlert } from 'lucide-react';
import { DupliquerTunnelResponse, TypeTunnel } from 'api/funnel';
import { funnelsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { ModalShell } from 'back-office/shared/components/ModalShell';
import { TYPE_TUNNEL_OPTIONS, tunnelTypeLabel } from '../detail/funnelEnums';
import { TunnelActionTarget } from '../tunnelActionTarget';

interface DuplicateTunnelModalProps {
  tunnel: TunnelActionTarget;
  /** Rafraîchissement de la liste (ou de la fiche) une fois la copie créée. */
  onSuccess?: () => void;
}

interface DuplicateFormValues {
  code: string;
  label: string;
  tunnelType: TypeTunnel;
}

/** Propositions du back, reproduites ici pour les montrer avant l'appel. */
const suggestedCode = (code?: string | null) => (code ? `${code}-COPIE` : '');
const suggestedLabel = (label?: string | null) => (label ? `${label} (copie)` : '');

/**
 * Duplication d'un tunnel.
 *
 * Le code est laissé vide par défaut, la proposition n'apparaissant qu'en filigrane :
 * un code imposé doit être libre, alors que sans code le back sait décaler la copie
 * (-COPIE-2, -COPIE-3…). Pré-remplir le champ ferait échouer la deuxième duplication
 * d'un même tunnel là où ne rien envoyer fonctionne toujours. Le libellé, lui, n'a
 * aucune contrainte d'unicité : il est pré-rempli.
 *
 * Le type, lui, est présélectionné sur celui de la source : le changer est possible mais
 * n'est pas anodin — les supports incompatibles avec le type demandé ne suivent pas.
 *
 * L'opération n'est pas idempotente — deux envois créent deux tunnels — d'où le bouton
 * désactivé pendant l'appel et l'absence de retry automatique en cas d'échec.
 */
export const DuplicateTunnelModal: FC<DuplicateTunnelModalProps> = ({ tunnel, onSuccess }) => {
  const formikRef = useRef<FormikProps<DuplicateFormValues>>(null);
  const navigate = useNavigate();
  const { closeDialog } = useDialog();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DupliquerTunnelResponse | null>(null);

  // Type de la source. Inconnu (écran qui ne le porterait pas), on n'envoie rien et le
  // back reconduit celui de la source : jamais de conversion par défaut.
  const sourceType =
    tunnel.tunnelType === TypeTunnel.Collectif || tunnel.tunnelType === TypeTunnel.Individuel
      ? tunnel.tunnelType
      : null;

  const initialValues: DuplicateFormValues = {
    code: '',
    label: suggestedLabel(tunnel.label),
    tunnelType: sourceType ?? TypeTunnel.Individuel,
  };

  const handleSubmit = async (values: DuplicateFormValues) => {
    if (isSubmitting) return;

    const code = values.code.trim();
    const label = values.label.trim();
    const tunnelType = sourceType === null ? undefined : values.tunnelType;

    setIsSubmitting(true);
    setError(null);
    try {
      const response = await funnelsAPI.duplicateFunnel({
        tunnelId: tunnel.id,
        // Corps entièrement optionnel : sans champ saisi, on n'envoie rien et le back
        // génère lui-même un code libre et un libellé.
        ...(code || label || tunnelType
          ? {
              dupliquerTunnelCommand: {
                ...(code && { code }),
                ...(label && { label }),
                ...(tunnelType && { tunnelType }),
              },
            }
          : {}),
      });

      if (!response.success) {
        // Échec fonctionnel : HTTP 200, message rédigé pour l'administrateur.
        setError(response.errorMessage || "La duplication du tunnel a échoué.");
        return;
      }

      setResult(response);
      onSuccess?.();
    } catch (thrown) {
      console.error('Duplication du tunnel impossible', thrown);
      const parsed = await parseApiError(thrown);
      setError(parsed.message || "La duplication du tunnel a échoué. Aucune donnée n'a été créée.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (result) {
    return (
      <DuplicateTunnelResult
        result={result}
        onOpenCopy={
          result.tunnelId
            ? () => {
                closeDialog();
                navigate(`/back-office/tunnels/${result.tunnelId}`);
              }
            : undefined
        }
      />
    );
  }

  return (
    <Formik innerRef={formikRef} initialValues={initialValues} onSubmit={handleSubmit}>
      <ModalShell
        title="Dupliquer le tunnel"
        subtitle={tunnel.label || tunnel.code || undefined}
        submit={{
          label: isSubmitting ? 'Duplication…' : 'Dupliquer',
          onClick: () => formikRef.current?.submitForm(),
          disabled: isSubmitting,
        }}
        closeLabel="Annuler"
      >
        <Form className="flex flex-col gap-5 rounded-xl border border-gray-200 bg-white p-5">
          <p className="text-sm text-gray-600">
            Garanties, franchises, plafonds, exclusions, commissions, co / ré-assureurs, frais,
            supports et plateformes autorisées sont repris. La copie est créée{' '}
            <span className="font-semibold">désactivée</span> : relisez-la avant de l'activer.
          </p>


          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <FormikInputText
                name="code"
                label="Code de la copie"
                placeholder={suggestedCode(tunnel.code)}
                disabled={isSubmitting}
              />
              <p className="mt-1 text-xs text-gray-400">
                Laisser vide pour laisser le back générer un code libre. Un code saisi doit être
                disponible : il identifie le tunnel partout (API partenaires, imports,
                paramétrage).
              </p>
            </div>
            <div>
              <FormikInputText name="label" label="Désignation de la copie" disabled={isSubmitting} />
              <p className="mt-1 text-xs text-gray-400">
                Laisser vide pour reprendre « {suggestedLabel(tunnel.label) || 'libellé (copie)'} ».
              </p>
            </div>
          </div>

          {sourceType !== null && (
            <div>
              <FormikDropdown
                name="tunnelType"
                label="Type de la copie"
                required
                items={TYPE_TUNNEL_OPTIONS.map((option) => ({
                  key: option.value,
                  label: option.label,
                  trailingLabel: option.hint,
                }))}
                disabled={isSubmitting}
              />
              <TypeChangeWarning sourceType={sourceType} />
            </div>
          )}

          <p className="text-xs text-gray-400">
            L'opération peut prendre quelques secondes sur un gros tunnel, et n'est pas
            réversible : ne validez qu'une fois.
          </p>
        </Form>
      </ModalShell>
    </Formik>
  );
};

/**
 * Avertissement affiché quand la copie change de type : le back ne reprend pas les
 * supports incompatibles avec le type demandé, et les liste dans notCopied.
 */
const TypeChangeWarning: FC<{ sourceType: TypeTunnel }> = ({ sourceType }) => {
  const { values } = useFormikContext<DuplicateFormValues>();

  if (values.tunnelType === sourceType) {
    return (
      <p className="mt-1 text-xs text-gray-400">
        Type du tunnel source, reconduit tel quel sur la copie.
      </p>
    );
  }

  return (
    <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-700">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
      <span>
        Changement de type : les supports incompatibles (SOLO sur un collectif, GROUP /
        CADRE sur un individuel) ne seront pas repris. Le compte rendu les listera.
      </span>
    </p>
  );
};

/**
 * Compte rendu de la duplication. Les deux listes viennent du back rédigées pour
 * l'administrateur : elles sont affichées telles quelles, sans reformulation ni tri, et
 * leur contenu comme leur longueur varient d'un tunnel à l'autre.
 */
const DuplicateTunnelResult: FC<{
  result: DupliquerTunnelResponse;
  onOpenCopy?: () => void;
}> = ({ result, onOpenCopy }) => {
  const copied = result.copied ?? [];
  const notCopied = result.notCopied ?? [];

  return (
    <ModalShell
      title="Tunnel dupliqué"
      subtitle={result.label || undefined}
      submit={onOpenCopy ? { label: 'Ouvrir la copie', onClick: onOpenCopy } : undefined}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
          {result.code && (
            <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">
              {result.code}
            </span>
          )}
          <span className="font-semibold text-gray-900">{result.label}</span>
          {tunnelTypeLabel(result.tunnelType) && (
            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
              {tunnelTypeLabel(result.tunnelType)}
            </span>
          )}
          {!result.isActive && (
            <span className="rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
              Inactif
            </span>
          )}
        </div>

        {copied.length > 0 && (
          <section className="rounded-xl border border-green-200 bg-green-50/60 p-4">
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-green-800">
              <Check className="h-4 w-4" />
              Repris dans la copie
            </h3>
            <ul className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
              {copied.map((line, index) => (
                <li key={`${index}-${line}`} className="text-sm text-green-900">
                  {line}
                </li>
              ))}
            </ul>
          </section>
        )}

        {notCopied.length > 0 && (
          <section className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              À reprendre à la main
            </h3>
            <ul className="flex flex-col gap-1.5">
              {notCopied.map((line, index) => (
                <li key={`${index}-${line}`} className="text-sm text-amber-900">
                  {line}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </ModalShell>
  );
};

export default DuplicateTunnelModal;
